import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import React, { useCallback, useEffect, useState } from 'react';
import { defaults } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import Select from 'react-select';

import LoaderComponent from '@components/LoaderComponent';
import { toastError } from '@components/Toast';

import BarChart from '@features/dashboard/components/admin-dashboard/BarChart';

import ApiClient from '@lib/apiClient';

import { addImageSuffix, changeDateFormat, getDateDifference } from '@utils/Util';

defaults.font.family = 'Open sans';

const PRO_PIC_DEFAULT = `${process.env.PUBLIC_URL}/images/pro_pic_default.svg`;

const AdminDashBoard = () => {
	const [teamPerfomanceData, setTeamPerfomanceData] = useState([]);
	const [brandSatisfactionData, setBrandSatisfactionData] = useState([]);
	const [orderStatus, setOrderStatus] = useState([]);
	const [brandStatus, setBrandStatus] = useState([]);
	const [, setSelectedBrand] = useState('');
	const [, setSelectedOrder] = useState('');
	const [orderPerfomance, setOrderPerfomance] = useState([]);
	const [loading, setLoading] = useState(false);
	const [teamLoading, setTeamLoading] = useState(false);
	const [brandLoading, setBrandLoading] = useState(false);
	const useStyles = makeStyles({
		tooltip: {
			fontSize: '12px'
		}
	});

	const getTeamPerformance = useCallback(async (value = '') => {
		const params = `?orderId=${value}`;
		setTeamLoading(true);
		await ApiClient.GET('getTeamPerformance', params)
			.then(({ data }) => {
				setTeamLoading(false);
				if (data) {
					setTeamPerfomanceData(data);
				}
			})
			.catch(({ response }) => {
				setTeamLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					// toastError("Something went wrong! Please try again.");
				}
			});
	}, []);

	const getBrandPerformance = useCallback(async (value = '') => {
		const params = `?brandId=${value}`;
		setBrandLoading(true);
		await ApiClient.GET('getBrandPerformance', params)
			.then(({ data }) => {
				setBrandLoading(false);
				if (data) {
					setBrandSatisfactionData(data);
				}
			})
			.catch(({ response }) => {
				setBrandLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					// toastError("Something went wrong! Please try again.");
				}
			});
	}, []);

	const getFilterByStatus = useCallback(async () => {
		let params = '?status=RUNNING';
		setLoading(true);
		await ApiClient.GET('getFilterByStatus', params)
			.then(({ data }) => {
				setLoading(false);
				if (data) {
					const result = data.map((item) => {
						return {
							value: item.orderId,
							label: item.name
						};
					});
					setOrderStatus(result);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					// toastError("Something went wrong! Please try again.");
				}
			});
	}, []);

	const getBrandStatus = useCallback(async () => {
		let params = '?status=RUNNING';
		setLoading(true);
		await ApiClient.GET('getBrandStatus', params)
			.then(({ data }) => {
				setLoading(false);
				if (data) {
					const result = data.map((item) => {
						return {
							value: item.id,
							label: item.name
						};
					});
					setBrandStatus(result);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					// toastError("Something went wrong! Please try again.");
				}
			});
	}, []);

	const getOrderPerfomance = useCallback(async () => {
		setLoading(true);
		await ApiClient.GET('getOrderPerfomance')
			.then(({ data }) => {
				setLoading(false);
				if (data) {
					setOrderPerfomance(data);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					// toastError("Something went wrong! Please try again.");
				}
			});
	}, []);

	useEffect(() => {
		// getBusinessOverView(initialStartDate, initialEndDate);
		getTeamPerformance();
		getBrandPerformance();
		getFilterByStatus();
		getBrandStatus();
		getOrderPerfomance();
	}, []);

	const getParcentage = (overdue, pending, completed, targetNumber) => {
		return Math.round((targetNumber * 100) / (overdue + pending + completed));
	};

	const getBrandParcentage = (
		aheadOfScheduleCount,
		onTimeCount,
		behindScheduleCount,
		overdueCount,
		targetNumber
	) => {
		return Math.round(
			(targetNumber * 100) / (aheadOfScheduleCount + onTimeCount + behindScheduleCount + overdueCount)
		);
	};

	const onSelectTeamPerfomance = (selected) => {
		if (selected === null) {
			setSelectedOrder('');
			getTeamPerformance();
		} else {
			setSelectedOrder(selected.value);
			getTeamPerformance(selected.value);
		}
	};

	const onBrandSatisfaction = (selected) => {
		if (selected === null) {
			setSelectedBrand('');
			getBrandPerformance();
		} else {
			setSelectedBrand(selected.value);
			getBrandPerformance(selected.value);
		}
	};

	const classes = useStyles();
	const styles = {
		container: (provided) => ({
			...provided,
			display: 'inline-block',
			width: '180px',
			minHeight: '1px',
			textAlign: 'left',
			border: 'none'
		}),
		control: (provided) => ({
			...provided,
			borderRadius: '0',
			minHeight: '1px',
			height: '42px',
			cursor: 'pointer'
		}),
		clearIndicator: (provided) => ({
			...provided,
			minHeight: '1px'
		}),
		input: (provided) => ({
			...provided,
			margin: '0',
			padding: '0',
			'grid-template-columns': 'auto'
		})
	};

	return (
		<LoaderComponent loading={loading || teamLoading || brandLoading}>
			<div className="top-header body-bg-gray">
				<div className="page-title-area  body-bg-gray pb-2">
					<h1 className="semibold-16">Dashboard</h1>
				</div>
			</div>
			<div className="full-dashboard">
				<BarChart />
				<div className="progress-info-team-brand">
					<div className="container-fluid">
						<div className="row gx-2">
							<div className="col-6 one-half">
								<div className="team-performance-top">
									<div className="col-title">
										<h3>Team performance</h3>
									</div>
									<div className="status-filter d-flex align-items-center justify-content-between">
										<div className="performence-status">
											<ul className="order-status d-flex">
												<li className="mini-fonts">
													<span className="progress-identifier green" />
													Completed
												</li>
												<li className="mini-fonts">
													<span className="progress-identifier blue" />
													Pending
												</li>
												<li className="mini-fonts">
													<span className="progress-identifier red" />
													Overdue
												</li>
											</ul>
										</div>
										<div className="order-filter">
											<Select
												styles={styles}
												options={orderStatus}
												isClearable
												onChange={onSelectTeamPerfomance}
												placeholder="Runnig orders"
												isSearchable={true}
											/>
										</div>
									</div>
								</div>
								<div className="progress-section">
									{!teamLoading && teamPerfomanceData.length === 0 ? (
										<div className="text-center">
											<p>No result found</p>
										</div>
									) : (
										teamPerfomanceData.map((item) => (
											<div
												className="single-person-progress d-flex align-items-center"
												key={item.userId}
											>
												<div className="person-info d-flex align-items-center">
													<img
														src={
															item.profilePicUrl
																? addImageSuffix(item.profilePicUrl, '_xicon')
																: PRO_PIC_DEFAULT
														}
														alt={item.name}
													/>
													<p className="mini-fonts">{item.name}</p>
												</div>
												<div className="progress-info">
													<div className="progress">
														<Tooltip
															classes={{ tooltip: classes.tooltip }}
															title={`${item.completedCount} Completed`}
															placement="top"
															arrow
														>
															<div
																className="progress-bar completed"
																role="progressbar"
																style={{
																	width:
																		getParcentage(
																			item.overdueCount,
																			item.pendingCount,
																			item.completedCount,
																			item.completedCount
																		) + '%'
																}}
																aria-valuemin={0}
																aria-valuemax={100}
															/>
														</Tooltip>
														<Tooltip
															classes={{ tooltip: classes.tooltip }}
															title={`${item.pendingCount} Pending`}
															placement="top"
															arrow
														>
															<div
																className="progress-bar pending"
																role="progressbar"
																style={{
																	width:
																		getParcentage(
																			item.overdueCount,
																			item.pendingCount,
																			item.completedCount,
																			item.pendingCount
																		) + '%'
																}}
																aria-valuemin={0}
																aria-valuemax={100}
															/>
														</Tooltip>
														<Tooltip
															classes={{ tooltip: classes.tooltip }}
															title={`${item.overdueCount} Overdue`}
															placement="top"
															arrow
														>
															<div
																className="progress-bar overdue"
																role="progressbar"
																style={{
																	width:
																		getParcentage(
																			item.overdueCount,
																			item.pendingCount,
																			item.completedCount,
																			item.overdueCount
																		) + '%'
																}}
																aria-valuemin={0}
																aria-valuemax={100}
															/>
														</Tooltip>
													</div>
												</div>
											</div>
										))
									)}
								</div>
							</div>

							<div className="col-6 one-half brand-satisfaction">
								<div className="team-performance-top">
									<div className="col-title">
										<h3>Brand satisfaction</h3>
									</div>
									<div className="status-filter d-flex align-items-center justify-content-between">
										<div className="performence-status">
											<ul className="order-status d-flex">
												<li className="mini-fonts">
													<span className="progress-identifier ahead-schedule" />
													Ahead of schedule
												</li>
												<li className="mini-fonts">
													<span className="progress-identifier green" />
													Ontime
												</li>
												<li className="mini-fonts">
													<span className="progress-identifier bg-warning" />
													Behind schedule
												</li>
												<li className="mini-fonts">
													<span className="progress-identifier red" />
													Overdue
												</li>
											</ul>
										</div>
										<div className="order-filter">
											<Select
												styles={styles}
												options={brandStatus}
												isClearable
												onChange={onBrandSatisfaction}
												placeholder="Brands"
												isSearchable={true}
											/>
										</div>
									</div>
								</div>
								<div className="progress-section">
									{!brandLoading && brandSatisfactionData.length === 0 ? (
										<div className="text-center">
											<p>No result found</p>
										</div>
									) : (
										brandSatisfactionData.map((item) => (
											<div
												className="single-person-progress d-flex align-items-center"
												key={item.brandId}
											>
												<div className="person-info d-flex align-items-center">
													<img
														src={
															item.brandLogoDocUrl
																? addImageSuffix(
																		item.brandLogoDocUrl,
																		'_xicon'
																	)
																: PRO_PIC_DEFAULT
														}
														alt={item.brandName}
													/>
													<span className="mini-fonts">{item.brandName}</span>
												</div>
												<div className="progress-info">
													<div className="progress">
														<Tooltip
															classes={{ tooltip: classes.tooltip }}
															title={`${item.aheadOfScheduleCount} Ahead of schedule`}
															placement="top"
															arrow
														>
															<div
																className="progress-bar ahead-schedule"
																role="progressbar"
																style={{
																	width:
																		getBrandParcentage(
																			item.aheadOfScheduleCount,
																			item.onTimeCount,
																			item.behindScheduleCount,
																			item.overdueCount,
																			item.aheadOfScheduleCount
																		) + '%'
																}}
																aria-valuemin={0}
																aria-valuemax={100}
															/>
														</Tooltip>
														<Tooltip
															classes={{ tooltip: classes.tooltip }}
															title={`${item.onTimeCount} Ontime`}
															placement="top"
															arrow
														>
															<div
																className="progress-bar completed"
																role="progressbar"
																style={{
																	width:
																		getBrandParcentage(
																			item.aheadOfScheduleCount,
																			item.onTimeCount,
																			item.behindScheduleCount,
																			item.overdueCount,
																			item.onTimeCount
																		) + '%'
																}}
																aria-valuemin={0}
																aria-valuemax={100}
															/>
														</Tooltip>
														<Tooltip
															classes={{ tooltip: classes.tooltip }}
															title={`${item.behindScheduleCount} Behind Schedule`}
															placement="top"
															arrow
														>
															<div
																className="progress-bar bg-warning"
																role="progressbar"
																style={{
																	width:
																		getBrandParcentage(
																			item.aheadOfScheduleCount,
																			item.onTimeCount,
																			item.behindScheduleCount,
																			item.overdueCount,
																			item.behindScheduleCount
																		) + '%'
																}}
																aria-valuemin={0}
																aria-valuemax={100}
															/>
														</Tooltip>
														<Tooltip
															classes={{ tooltip: classes.tooltip }}
															title={`${item.overdueCount} Overdue`}
															placement="top"
															arrow
														>
															<div
																className="progress-bar overdue"
																role="progressbar"
																style={{
																	width:
																		getBrandParcentage(
																			item.aheadOfScheduleCount,
																			item.onTimeCount,
																			item.behindScheduleCount,
																			item.overdueCount,
																			item.overdueCount
																		) + '%'
																}}
																aria-valuemin={0}
																aria-valuemax={100}
															/>
														</Tooltip>
													</div>
												</div>
											</div>
										))
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
				{/* End Team and brand Information section  */}
				{/* Start Running Orders section  */}
				<div className="running-orders-info-section progress-info-team-brand">
					<div className="container-fluid">
						<div className="row">
							<div className="col-12 px-0">
								<div className="team-performance-top">
									<div className="col-title">
										<h3>
											Running orders{' '}
											<span className="order-count">({orderPerfomance.length})</span>
										</h3>
									</div>
									<div className="status-filter">
										<div className="performence-status">
											<ul className="order-status d-flex">
												<li className="mini-fonts">
													<span className="progress-identifier green" />
													Completed
												</li>
												<li className="mini-fonts">
													<span className="progress-identifier blue" />
													Pending
												</li>
												<li className="mini-fonts">
													<span className="progress-identifier red" />
													Overdue
												</li>
											</ul>
										</div>
									</div>
								</div>
								<div className="progress-section">
									{orderPerfomance?.map((item) => (
										<div
											className="single-person-progress d-flex align-items-center"
											key={item.orderId}
										>
											<div className="person-info d-flex align-items-center">
												<div className="four-images d-flex">
													{item.productDesignPaths.length &&
														item.productDesignPaths.map((value, index) => (
															<div className="single-one" key={index}>
																<img
																	src={
																		value
																			? addImageSuffix(value, '_xicon')
																			: PRO_PIC_DEFAULT
																	}
																	alt={item.name}
																/>
															</div>
														))}
													{item.productDesignPaths.length < 1 && (
														<div className="single-one"></div>
													)}
													{item.productDesignPaths.length < 2 && (
														<div className="single-one"></div>
													)}
													{item.productDesignPaths.length < 3 && (
														<div className="single-one"></div>
													)}
													{item.productDesignPaths.length < 4 && (
														<div className="single-one"></div>
													)}
												</div>

												<div className="order-name">
													<p>
														<Link to={`/orders/view/${item.orderId}?workTab=TNA`}>
															{item.orderName}
														</Link>
													</p>
												</div>
												<div className="assigned-person">
													<p>{item.projectManagerName}</p>
												</div>
												<div className="date-info">
													<p>
														{changeDateFormat(item.deliveryDate, '', 'MMM DD')}
														<span className="date-count">
															{' '}
															(
															{getDateDifference(undefined, item.deliveryDate) +
																'd'}
															)
														</span>
													</p>
												</div>
											</div>
											<div className="progress-info">
												<div className="progress">
													<Tooltip
														classes={{ tooltip: classes.tooltip }}
														title={`${item.completedCount} Completed`}
														placement="top"
														arrow
													>
														<div
															className="progress-bar completed"
															role="progressbar"
															style={{
																width:
																	getParcentage(
																		item.overdueCount,
																		item.pendingCount,
																		item.completedCount,
																		item.completedCount
																	) + '%'
															}}
															aria-valuemin={0}
															aria-valuemax={100}
														/>
													</Tooltip>

													<Tooltip
														classes={{ tooltip: classes.tooltip }}
														title={`${item.pendingCount} Pending`}
														placement="top"
														arrow
													>
														<div
															className="progress-bar pending"
															role="progressbar"
															style={{
																width:
																	getParcentage(
																		item.overdueCount,
																		item.pendingCount,
																		item.completedCount,
																		item.pendingCount
																	) + '%'
															}}
															aria-valuemin={0}
															aria-valuemax={100}
														/>
													</Tooltip>
													<Tooltip
														classes={{ tooltip: classes.tooltip }}
														title={`${item.overdueCount} Overdue`}
														placement="top"
														arrow
													>
														<div
															className="progress-bar overdue"
															role="progressbar"
															style={{
																width:
																	getParcentage(
																		item.overdueCount,
																		item.pendingCount,
																		item.completedCount,
																		item.overdueCount
																	) + '%'
															}}
															aria-valuemin={0}
															aria-valuemax={100}
														/>
													</Tooltip>
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default AdminDashBoard;
